import React, { useEffect, useState } from "react";
import api from "../../api";

import CreateStockPrices from "./CreateStockPrices";
import StockPricesTable from "./StockPricesTable";
import StockPricesCards from "./StockPricesCards";
import Pagination from "./Pagination";
import { Container } from "../layout/Container"
import { PageHeader } from "../layout/PageHeader"
import { toast } from "../shared/ToastProvider";
import EmptyState from "../shared/EmptyState";
import DeleteConfirmation from "../shared/DeleteConfirmation";

const StockPrices = () => {
    const [stockPrices, setStockPrices] = useState([]);
    const [stocks, setStocks] = useState([]);
    const [formData, setFormData] = useState({
        price_date: "",
        open_price: "",
        close_price: "",
        high_price: "",
        low_price: "",
        volume: "",
        fk_stock_symbol: "",
    });
    const [isUpdating, setIsUpdating] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 100;

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    // Recuperer les Stocks
    useEffect(() => {
        const fetchStocks = async () => {
            try {
                const response = await api.get("/stocks");
                setStocks(response.data);
            } catch (error) {
                console.log("Erreur lors de la récupération des stocks", error);
            }
        };

        fetchStocks();
    }, []);

    // Fonction pour récupérer les données
    useEffect(() => {
        const fetchStockPrices = async () => {
            setLoading(true);
            try {
                const response = await api.get("/api/stockprices");
                setStockPrices(response.data);
            } catch (error) {
                console.log("Erreur lors de la récupération des prix des actions", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStockPrices();
    }, []);

    // Fonction pour gérer les changements dans le formulaire
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Fonction pour soumettre les données (Create ou Update)
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isUpdating) {
                const { price_date, fk_stock_symbol } = formData;
                await api.put(`/api/stockprices/${price_date}/${fk_stock_symbol}`, formData);
                setStockPrices((prev) =>
                    prev.map((price) =>
                        price.price_date === price_date && price.fk_stock_symbol === fk_stock_symbol
                            ? { ...price, ...formData }
                            : price
                    )
                );
                setIsUpdating(false);
                toast.success('Prix mis à jour avec succès');
            } else {
                const response = await api.post("/api/stockprices", formData);
                setStockPrices((prev) => [...prev, response.data]);
                toast.success('Prix ajouté avec succès');
            }
            setFormData({
                price_date: "",
                open_price: "",
                close_price: "",
                high_price: "",
                low_price: "",
                volume: "",
                fk_stock_symbol: "",
            });
        } catch (error) {
            toast.error("Erreur lors de l'ajout");
            console.error("Erreur lors de l'ajout ou de la mise à jour :", error);
        }
    };

    // Fonction pour passer en mode mise à jour
    const handleUpdateClick = (price) => {
        setFormData(price);
        setIsUpdating(true);
    };

    // Fonction pour supprimer une ligne
    const handleDelete = (priceDate, stockSymbol) => {
        setDeleteTarget({ priceDate, stockSymbol });
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        setIsDeleteDialogOpen(false);
        if (!deleteTarget) return;
        const { priceDate, stockSymbol } = deleteTarget;
        try {
            const response = await api.delete(`/api/stockprices/${priceDate}/${stockSymbol}`);
            if (response.status === 200) {
                setStockPrices((prev) =>
                    prev.filter(
                        (price) =>
                            !(price.price_date === priceDate && price.fk_stock_symbol === stockSymbol)
                    )
                );
                toast.success('Prix supprimé avec succès');
            }
        } catch (error) {
            toast.error('Erreur lors de la suppression');
            console.error("Erreur lors de la suppression du prix d'action :", error);
        } finally {
            setDeleteTarget(null);
        }
    };

    // Pagination - Calcul des données affichées
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = stockPrices.slice(startIndex, startIndex + itemsPerPage);

    // Pagination - Gestion des boutons
    const totalPages = Math.ceil(stockPrices.length / itemsPerPage);

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePageClick = (page) => {
        setCurrentPage(page);
    };

    return (
        <Container className="py-6 md:py-10">
            <PageHeader title="Prix des actions" />

            {/* Formulaire de création/mise à jour */}
            <CreateStockPrices
                formData={formData}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                isUpdating={isUpdating}
                stocks={stocks}
                setFormData={setFormData}
            />

            {stockPrices.length === 0 && !loading ? (
                <EmptyState
                    title="Aucun prix trouvé"
                    subtitle="Ajoutez votre premier prix pour commencer."
                    actionLabel="Ajouter"
                    onAction={() => document.getElementById('stockprice-date')?.focus()}
                />
            ) : (
                <>
                    {/* Tableau des données */}
                    <div className="hidden md:block">
                        <StockPricesTable
                            currentItems={currentItems}
                            handleUpdateClick={handleUpdateClick}
                            handleDelete={handleDelete}
                        />
                    </div>
                    <div className="md:hidden">
                        <StockPricesCards
                            items={currentItems}
                            loading={loading}
                            onUpdateClick={handleUpdateClick}
                            onDeleteRequest={(priceDate, stockSymbol) => handleDelete(priceDate, stockSymbol)}
                        />
                    </div>

                    {/* Pagination */}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handlePreviousPage={handlePreviousPage}
                        handleNextPage={handleNextPage}
                        handlePageClick={handlePageClick}
                    />
                </>
            )}

            <DeleteConfirmation
                isOpen={isDeleteDialogOpen}
                onClose={() => { setIsDeleteDialogOpen(false); setDeleteTarget(null); }}
                onConfirm={handleDeleteConfirmed}
                title="Confirmer la suppression"
                description="Voulez-vous vraiment supprimer ce prix ? Cette action est irréversible."
                confirmText="Supprimer"
            />
        </Container>
    );
};

export default StockPrices;
